import React, { useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

/**
 * Pagination — shared component matching the Search dashboard's pattern.
 *
 * Features: First / Previous / numeric pages with "…" elision /
 * Next / Last + jump-to-page input. Used by Dashboard search results
 * and the Disqualified list.
 *
 * `filteredCount` (optional) replaces the "Showing X-Y of Z" line with
 * "Showing N items on this page (page X of Y)" — used when an upstream
 * filter (e.g. hide-closed) shrinks the visible count.
 */
const Pagination = ({ currentPage, totalItems, itemsPerPage, onPageChange, filteredCount = null, itemLabel = 'result' }) => {
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startItem = (currentPage - 1) * itemsPerPage + 1;
  const endItem = Math.min(currentPage * itemsPerPage, totalItems);

  const [jumpInput, setJumpInput] = useState('');
  const submitJump = (e) => {
    if (e) e.preventDefault();
    const n = parseInt(jumpInput, 10);
    if (Number.isFinite(n)) {
      const clamped = Math.min(Math.max(1, n), totalPages);
      onPageChange(clamped);
      setJumpInput('');
    }
  };

  const getVisiblePages = () => {
    const delta = 2;
    const range = [];
    const rangeWithDots = [];

    for (let i = Math.max(2, currentPage - delta); i <= Math.min(totalPages - 1, currentPage + delta); i++) {
      range.push(i);
    }

    if (currentPage - delta > 2) {
      rangeWithDots.push(1, '...');
    } else {
      rangeWithDots.push(1);
    }

    rangeWithDots.push(...range);

    if (currentPage + delta < totalPages - 1) {
      rangeWithDots.push('...', totalPages);
    } else if (totalPages > 1) {
      rangeWithDots.push(totalPages);
    }

    return rangeWithDots;
  };

  if (totalPages <= 1) return null;

  return (
    <div className="pagination">
      <div className="pagination-info">
        {filteredCount !== null ? (
          <>Showing {filteredCount} {itemLabel}{filteredCount !== 1 ? 's' : ''} on this page (page {currentPage} of {totalPages})</>
        ) : (
          <>Showing {startItem}-{endItem} of {totalItems.toLocaleString()} {itemLabel}s</>
        )}
      </div>

      <div className="pagination-controls">
        <button
          className="pagination-btn"
          onClick={() => onPageChange(1)}
          disabled={currentPage === 1}
          title="First page"
        >
          «
        </button>
        <button
          className="pagination-btn"
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 1}
        >
          <ChevronLeft size={16} />
          Previous
        </button>

        <div className="pagination-numbers">
          {getVisiblePages().map((page, index) => (
            <button
              key={index}
              className={`pagination-number ${page === currentPage ? 'active' : ''} ${page === '...' ? 'dots' : ''}`}
              onClick={() => typeof page === 'number' && onPageChange(page)}
              disabled={page === '...'}
            >
              {page}
            </button>
          ))}
        </div>

        <button
          className="pagination-btn"
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
        >
          Next
          <ChevronRight size={16} />
        </button>
        <button
          className="pagination-btn"
          onClick={() => onPageChange(totalPages)}
          disabled={currentPage === totalPages}
          title="Last page"
        >
          »
        </button>

        <form className="pagination-jump" onSubmit={submitJump}>
          <label htmlFor="page-jump">Go to</label>
          <input
            id="page-jump"
            type="number"
            min={1}
            max={totalPages}
            placeholder={String(currentPage)}
            value={jumpInput}
            onChange={(e) => setJumpInput(e.target.value)}
            onBlur={() => jumpInput && submitJump()}
            aria-label={`Go to page (1-${totalPages})`}
          />
          <span className="pagination-jump-of">/ {totalPages.toLocaleString()}</span>
        </form>
      </div>
    </div>
  );
};

export default Pagination;
